import { FieldErrors, useFormContext } from 'react-hook-form'
import { BefehlFormField } from '../Generic'
import { Input } from '../../form'
import { cn } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState, useContext } from 'react'
import Button from '../../Button'
import { DeleteIcon24 } from '../../../icons'
import SlideToUnlock from '../../SlideToUnlock'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'

export interface KmZeileFormProps {
  zeile: number
  fieldPrefix: string
  readOnly: boolean
  testId: string
  displaySlideToUnlock: boolean
  showRemoveButton: boolean
  onRemove?: () => void
  disabled?: boolean
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

export function KmZeileForm({
  zeile,
  fieldPrefix,
  readOnly,
  testId,
  showRemoveButton,
  onRemove,
  displaySlideToUnlock,
  disabled = false,
  activateEmptyFieldWarnings,
  errors
}: Readonly<KmZeileFormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formContext = useFormContext()
  const { control, formState } = formContext

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const fieldPaths = useMemo(
    () => ({
      kmPath: `${fieldPrefix}`
    }),
    [fieldPrefix]
  )

  const context = useContext(MarkAuftraegeAsDoneContext)
  const befehlFrontendId = context?.befehlFrontendId
  const isQuittiert = context?.isBefehlsnachrichtQuittiert

  const [confirmed, setConfirmed] = useState<boolean>(
    context?.isDone(befehlFrontendId + '-8_5' + zeile) || false
  )

  const updateSliderState = (value: boolean) => {
    context?.setMarkedAsDone(befehlFrontendId + '-8_5' + zeile, value)
    setConfirmed(value)
  }

  const renderRemoveButton = useCallback(() => {
    if (!showRemoveButton || disabled) return null
    return (
      <Button
        type="button"
        variant="tertiary-plain"
        onClick={onRemove}
        className="text-gray-400 hover:text-red-600 flex items-center gap-0.25 min-h-[44px] min-w-[44px] px-1 py-0.5 -mr-1 -mt-0.5"
        data-testid={`${testId}-zeile-${zeile}-remove-button`}
        disabled={disabled}
      >
        <DeleteIcon24
          data-testid={`${testId}-zeile-${zeile}-remove-button-icon`}
        />
        <span
          className="text-sm"
          data-testid={`${testId}-zeile-${zeile}-remove-button-text`}
        >
          {t('auftraege.1_10.zeile.remove')}
        </span>
      </Button>
    )
  }, [showRemoveButton, disabled, onRemove, t, testId, zeile])

  const translationKeys = useMemo(
    () => ({
      kmInputLabel: 'auftraege.8_50.fields.km_label'
    }),
    []
  )

  return (
    <div className="@container relative -ml-3 sm:ml-0">
      {confirmed && isQuittiert && (
        <div
          className="absolute z-30 inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
          data-testid={`${testId}-gray-out-overlay`}
        />
      )}
      <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
        <div className="@[400px]:w-8 @[400px]:text-right">
          <span data-testid={`${testId}-zeile-${zeile}-text`}>
            {zeile == 1
              ? t('auftraege.8_50.fields.in_label')
              : t('auftraege.8_50.fields.und_label')}
          </span>
        </div>
        <div className="flex-1">
          <div
            className="border rounded-xl border-cool-gray-200 px-1 pt-1 pb-1 relative"
            data-testid={`${testId}-zeile-${zeile}-container`}
          >
            {showRemoveButton && (
              <div className="absolute top-0.5 right-1 z-10">
                {renderRemoveButton()}
              </div>
            )}
            <div className={cn('flex flex-col', showRemoveButton && 'mt-2.5')}>
              <div className="flex w-full">
                <div className="w-full">
                  <BefehlFormField
                    name={fieldPaths.kmPath}
                    control={control}
                    label={t(translationKeys.kmInputLabel)}
                    readOnly={readOnly}
                    disabled={disabled}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    data-testid={`${testId}-zeile-${zeile}-km`}
                    variant="solid"
                    render={({ field: kmField, isDisabled: isKmDisabled }) => (
                      <Input
                        {...kmField}
                        label={t(translationKeys.kmInputLabel)}
                        errors={fieldErrors}
                        className="w-full"
                        data-testid={`${testId}-zeile-${zeile}-km-inner`}
                        disabled={isKmDisabled}
                      />
                    )}
                  />
                </div>
              </div>
              <div>
                <span
                  data-testid={`${testId}-zeile-${zeile}-km-label`}
                >{`8.5${zeile}.1 km`}</span>
              </div>
            </div>
            {context && displaySlideToUnlock && (
              <SlideToUnlock
                className="pt-0.75"
                data-testid={`${testId}-abgearbeitet-markieren-slider`}
                confirmed={confirmed}
                onConfirmedChange={updateSliderState}
                variant="secondary-solid"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
